import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export type FilterValue = string | number | boolean | null;

export type FilterControl<T extends Record<string, FilterValue>> = {
  filters: T;
  setFilters: Dispatch<SetStateAction<T>>;
  setFilter: <K extends keyof T>(key: K, value: T[K]) => void;
  removeFilter: (key: keyof T) => void;
  clearFilters: () => void;
};

export function useFilterControl<T extends Record<string, FilterValue>>(
  initialFilters: T,
): FilterControl<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<T>(() => {
    const urlFilters: Partial<T> = {};
    for (const [key, value] of searchParams.entries()) {
      urlFilters[key as keyof T] = value as T[keyof T];
    }
    return { ...initialFilters, ...urlFilters };
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const currentSearchParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value != null) {
          currentSearchParams.set(key, String(value));
        }
      });

      const currentParamsString = currentSearchParams.toString();
      const searchParamsString = searchParams.toString();

      if (currentParamsString !== searchParamsString) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          Object.keys(initialFilters).forEach((key) => newParams.delete(key));
          Object.entries(filters).forEach(([key, value]) => {
            if (value != null) {
              newParams.set(key, String(value));
            }
          });
          return newParams;
        });
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [filters, setSearchParams, initialFilters, searchParams]);

  const setFilter = <K extends keyof T>(key: K, value: T[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const removeFilter = (key: keyof T) => {
    setFilters((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _, ...rest } = prev;
      return { ...initialFilters, ...rest } as T;
    });
  };

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  return { filters, setFilters, setFilter, removeFilter, clearFilters };
}
